/* EN-4396: which level the orders pages read, the account (every division) or
   the signed-in division. Server contract: apps/skupreme-api/apps/api/v1/ordersPg/scopeTypes.ts.

   Kept per division in localStorage, so switching division (a new token) does
   not carry the account level along unless it was chosen there. Storage can
   be missing or throw (private window): the division level is the fallback. */
import { useSyncExternalStore } from 'react';

export type OrdersScopeLevel = 'account' | 'division';

export type DivisionRef = { id: string; name: string };

export type AccountViewResponse =
  | { available: false }
  | {
      available: true;
      account: { id: string; name: string };
      divisions: DivisionRef[];
    };

/* Request params the orders API reads (scopeTypes.ts SCOPE_PARAM / DIVISION_FILTER_PARAM). */
export const SCOPE_PARAM = 'scope';
export const DIVISION_FILTER_PARAM = 'division';

const KEY = 'skupreme.ordersScope';
const listeners = new Set<() => void>();

const read = (divisionId: string | undefined): OrdersScopeLevel => {
  if (!divisionId) return 'division';
  try {
    return window.localStorage.getItem(`${KEY}:${divisionId}`) === 'account'
      ? 'account'
      : 'division';
  } catch {
    return 'division';
  }
};

export const setOrdersScopeLevel = (
  divisionId: string | undefined,
  level: OrdersScopeLevel,
) => {
  if (!divisionId) return;
  try {
    if (level === 'account') {
      window.localStorage.setItem(`${KEY}:${divisionId}`, 'account');
    } else {
      window.localStorage.removeItem(`${KEY}:${divisionId}`);
    }
  } catch {
    /* no storage: the level lasts until reload only through listeners */
  }
  listeners.forEach(l => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/* The stored level for a division. Callers must also check the account view
   is available (useAccountView) before sending scope=account. */
export const useOrdersScopeLevel = (
  divisionId: string | undefined,
): OrdersScopeLevel =>
  useSyncExternalStore(
    subscribe,
    () => read(divisionId),
    () => 'division',
  );

/* Params to add to every orders read (list, counts, live, detail, logs) at
   the account level; empty at the division level. */
export const scopeParams = (
  level: OrdersScopeLevel,
  divisionFilter?: readonly string[],
): Record<string, string> => {
  if (level !== 'account') return {};
  const params: Record<string, string> = { [SCOPE_PARAM]: 'account' };
  if (divisionFilter && divisionFilter.length > 0) {
    params[DIVISION_FILTER_PARAM] = divisionFilter.join(',');
  }
  return params;
};
